import { ICustomer, IUser } from '~common/interfaces';
import httpClient from '~utils/httpClient';
import { API_ENDPOINTS, DELETE_TYPES } from './constants';

export function createCustomer(data: ICustomer) {
  return httpClient.post(API_ENDPOINTS.customer, data);
}
export const getOrganizationsWithCustomers = async () => {
  const data = await httpClient.get(API_ENDPOINTS.getOrganizationsWithCustomers);
  return data;
};

export const getCustomers = () => httpClient.get(API_ENDPOINTS.customersShortInfo);

export const getCustomer = (id: number) => httpClient.get(`${API_ENDPOINTS.customer}/${id}`);
export const getCustomerShort = (id: number) => httpClient.get(`${API_ENDPOINTS.customerShort}/${id}`);

export const getCurrentCustomer = () => httpClient.get(API_ENDPOINTS.currentCustomer);
export const getCurrentCustomerShort = () => httpClient.get(API_ENDPOINTS.currentCustomerShort);

export const getCurrentCustomerApplications = () => httpClient.get(API_ENDPOINTS.currentCustomerApplications);
export const getCustomerApplications = (id: number) => httpClient.get(`${API_ENDPOINTS.customerApplications}/${id}`);

export const getCustomerCreationData = () => httpClient.get(API_ENDPOINTS.getCustomerCreationData);

export const updateCustomer = (data: ICustomer, customerId: number) => customerId ? httpClient.patch(`${API_ENDPOINTS.customer}/${customerId}`, data) : httpClient.patch(`${API_ENDPOINTS.currentCustomer}`, data);

export const getExternalIdPs = () => httpClient.get(API_ENDPOINTS.getExternalIdPs);

export const fetchCustomerForEdit = async (id: number) => {
  const getCustomerToEdit = id ? getCustomer(id) : getCurrentCustomer();
  //@ts-ignore
  const [{ value: customer }, { value: creationData }, { value: externalIdPs }] = await Promise.allSettled([getCustomerToEdit, getCustomerCreationData(), getExternalIdPs()]);

  return {
    data: {
      customer: { ...customer.data, externalIdentifications: customer.data.externalIdentifications?.externalIdentificationNames },
      types: creationData.data.types,
      externalIdPs: externalIdPs?.data,
    }
  };
};

export const changeAccountManager = (customerId: number, subjectId: string) => customerId
  ? httpClient.patch(`${API_ENDPOINTS.customerAccountManager}/${customerId}`, { subjectId })
  : httpClient.patch(`${API_ENDPOINTS.currentCustomerAccountManager}`, { subjectId });

export const changeAccountRepresentative = (customerId: number, subjectId: string) => httpClient.patch(`${API_ENDPOINTS.customerAccountRepresentative}/${customerId}`, { subjectId });

export const removeAccountManager = (customerId: number) => {
  return customerId ? httpClient.delete(`${API_ENDPOINTS.customerAccountManager}/${customerId}`) : httpClient.delete(`${API_ENDPOINTS.currentCustomerAccountManager}`);
};

export const removeAccountRepresentative = (customerId: number) => httpClient.delete(`${API_ENDPOINTS.customerAccountRepresentative}/${customerId}`);

export const addUserToCustomer = (user: IUser, customerId: number) => customerId ? httpClient.post(`${API_ENDPOINTS.getCustomerUsers}/${customerId}`, user) : httpClient.post(`${API_ENDPOINTS.getCurrentCustomerUsers}`, user);

export const getCustomerUsers = (customerId: number) => customerId ? httpClient.get(`${API_ENDPOINTS.getCustomerUsers}/${customerId}`) : httpClient.get(`${API_ENDPOINTS.getCurrentCustomerUsers}`);

export const getCustomerUsersWithRole = (customerId: number) => customerId ? httpClient.get(`${API_ENDPOINTS.getCustomerUsersWithRoles}/${customerId}`) : httpClient.get(`${API_ENDPOINTS.getCurrentCustomerUsersWithRoles}`);

export const fetchCustomerLicenses = (id: number) => (id ? httpClient.get(`${API_ENDPOINTS.getCustomerLicenses}/${id}`) : httpClient.get(`${API_ENDPOINTS.getCurrentCustomerLicenses}`));

export const removeUser = (userId: number, customerId: number) => customerId ? httpClient.delete(`${API_ENDPOINTS.getCustomerUsers}/${userId}`) : httpClient.delete(`${API_ENDPOINTS.getCurrentCustomerUsers}/${userId}`);

export const editUser = (user: IUser, customerId: number) => customerId ? httpClient.patch(`${API_ENDPOINTS.getCustomerUsers}/${customerId}`, user) : httpClient.patch(`${API_ENDPOINTS.getCurrentCustomerUsers}`, user);

export const getCustomerAccountRepresentative = () => httpClient.get(API_ENDPOINTS.getCustomerAccountRepresentative);
export const checkUserAssignedToCustomer = () => httpClient.get(API_ENDPOINTS.checkUserAssignedToCustomer);

export const addExternalUserToCustomer = (userIds: number[], customerId: number) => customerId
  ? httpClient.post(`${API_ENDPOINTS.getCustomerUsersExternal}/${customerId}`, { userIds })
  : httpClient.post(API_ENDPOINTS.getCurrentCustomerUsersExternal, { userIds });

export const removeExternalUserFromCustomer = (subjectId: string, customerId: number) => customerId
  ? httpClient.delete(`${API_ENDPOINTS.getCustomerUsersExternal}/${customerId}?subjectId=${subjectId}`)
  : httpClient.delete(`${API_ENDPOINTS.getCurrentCustomerUsersExternal}?subjectId=${subjectId}`);

export const deleteCustomer = (customer: ICustomer) => {
  const { customerId, deletedAt } = customer;
  const deleteType = deletedAt ? DELETE_TYPES.HARD : DELETE_TYPES.SOFT;
  return httpClient.delete(`${API_ENDPOINTS.customers}/${customerId}?deleteType=${deleteType}`);
};

export const restoreCustomer = (customerId: number) => httpClient.get(`/customer-restore/${customerId}`);

export const changeLocalLoginStrategyBulk = (type: boolean, customerId: number) => httpClient.patch(`${API_ENDPOINTS.changeLocalLoginStrategyBulk}/${customerId}?type=${type}`);

export const changeCustomerFeatureAccess = (type: string, customerId:number, value: boolean) => httpClient.patch(`${API_ENDPOINTS.changeCustomerFeatureAccess}/${customerId}?type=${type}&value=${value}`);

export const updateMFABulk = (enableMFA: boolean, customerId: number) => httpClient.patch(`${API_ENDPOINTS.updateMFABulk}/${customerId}?enableMFA=${enableMFA}`);

export const fetchOliasoftUsers = () => httpClient.get(API_ENDPOINTS.oliasoftUsers);

export const requestExternalUserByEmail = (email: string, customerId: number) => httpClient.post(API_ENDPOINTS.requestExternalUser, { email, customerId });

export const customerDataWipe = (customerId: number) => httpClient.delete(`${API_ENDPOINTS.dataWipe}/${customerId}`)